import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AssemblyAIRecordingOptions {
  language?: string;
  speakers?: string[];
}

interface TranscriptSegment {
  start: number;
  end: number;
  text: string;
  speaker: string;
  confidence: number;
}

interface AssemblyAIRecordingState {
  isRecording: boolean;
  isPaused: boolean;
  isProcessing: boolean;
  transcript: string;
  segments: TranscriptSegment[];
  error: string | null;
  isSupported: boolean;
  language: string;
  liveTranscript: string;
  cumulativeTranscript: string;
  processedSegments: number;
  sessionId: string;
  sessionTranscript: string;
  sessionSegments: TranscriptSegment[];
  sessionDuration: number;
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'failed' | 'permission_denied';
  apiKeyValid: boolean | null;
  shouldFallback: boolean;
  diagnostics: {
    apiKeyValid: boolean | null;
    networkReachable: boolean | null;
    websocketSupported: boolean;
    lastError: string | null;
    connectionAttempts: number;
    permissionDenied: boolean;
  };
}

export const useAssemblyAIRecording = (options: AssemblyAIRecordingOptions = {}) => {
  const {
    language: defaultLanguage = 'en',
    speakers = ['Therapist', 'Client']
  } = options;

  const [state, setState] = useState<AssemblyAIRecordingState>({
    isRecording: false,
    isPaused: false,
    isProcessing: false,
    transcript: '',
    segments: [],
    error: null,
    isSupported: typeof window !== 'undefined' && 'MediaRecorder' in window,
    language: defaultLanguage,
    liveTranscript: '',
    cumulativeTranscript: '',
    processedSegments: 0,
    sessionId: `assembly_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    sessionTranscript: '',
    sessionSegments: [],
    sessionDuration: 0,
    connectionStatus: 'disconnected',
    apiKeyValid: null,
    shouldFallback: false,
    diagnostics: {
      apiKeyValid: null,
      networkReachable: null,
      websocketSupported: typeof WebSocket !== 'undefined',
      lastError: null,
      connectionAttempts: 0,
      permissionDenied: false
    }
  });

  // Keep internal language in sync with consumer option
  useEffect(() => {
    setState(prev => (
      prev.language === defaultLanguage ? prev : { ...prev, language: defaultLanguage }
    ));
  }, [defaultLanguage]);

  const websocketRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const sessionStartTime = useRef<number>(0);
  const isConnectedRef = useRef<boolean>(false);
  const isPausedRef = useRef<boolean>(false);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef<number>(0);
  const maxReconnectAttempts = 3;
  const audioChunkCountRef = useRef<number>(0);
  const isAudioWorkletSupported = useRef<boolean>(false);

  // 检查 AudioWorklet 支持
  useEffect(() => {
    if (typeof window !== 'undefined' && window.AudioContext) {
      const isModernBrowser = 'audioWorklet' in AudioContext.prototype;
      const userAgent = navigator.userAgent.toLowerCase();
      
      const isLegacyBrowser = userAgent.includes('chrome/') && 
        parseInt(userAgent.match(/chrome\/(\d+)/)?.[1] || '0') < 66;
      
      isAudioWorkletSupported.current = isModernBrowser && !isLegacyBrowser;
      
      const languageForLog = state.language === 'zh' ? '中文' : 'English';
      console.log(`🎧 AudioWorklet ${languageForLog} support:`, isAudioWorkletSupported.current);
    }
  }, [state.language]);

  // 连接到 AssemblyAI Streaming API v3
  const connectToAssemblyAI = useCallback(async () => {
    try {
      const languageForLog = state.language === 'zh' ? '中文' : 'English';
      const actionText = state.language === 'zh' ? '启动 AssemblyAI 流式转录连接' : 'Starting AssemblyAI streaming connection';
      console.log(`🔗 ${actionText}...`);
      
      setState(prev => ({ 
        ...prev, 
        connectionStatus: 'connecting', 
        error: null,
        diagnostics: { ...prev.diagnostics, connectionAttempts: prev.diagnostics.connectionAttempts + 1 }
      }));
      
      // 从Supabase获取API Key
      const keyFetchText = state.language === 'zh' ? '从 Supabase 获取已验证的 API 密钥' : 'Fetching validated API key from Supabase';
      console.log(`📡 ${keyFetchText}...`);
      
      const { data, error } = await supabase.functions.invoke('get-assembly-key');
      
      if (error || !data?.apiKey) {
        const errorText = state.language === 'zh' 
          ? `获取 AssemblyAI API 密钥失败: ${error?.message || '未返回 API 密钥'}`
          : `Failed to get AssemblyAI API key: ${error?.message || 'No API key returned'}`;
        throw new Error(errorText);
      }
      
      const apiKey = data.apiKey;
      const isValidated = data.isValid;
      
      const validationText = state.language === 'zh' ? '获得 API 密钥，服务器验证' : 'Got API key, server validation';
      console.log(`✅ ${validationText}:`, isValidated);

      // 更新API Key状态
      setState(prev => ({
        ...prev,
        apiKeyValid: isValidated,
        diagnostics: { 
          ...prev.diagnostics, 
          apiKeyValid: isValidated, 
          networkReachable: true 
        }
      }));

      if (isValidated === false) {
        const invalidKeyText = state.language === 'zh' 
          ? 'API 密钥验证失败 - 密钥无效或已过期'
          : 'API key validation failed - invalid or expired key';
        throw new Error(invalidKeyText);
      }

      // 构造 WebSocket URL，使用正确的采样率和语言
      const urlParams = new URLSearchParams({
        sample_rate: '16000', // 修正为16kHz
        format_turns: 'true',
        language_code: state.language, // 使用动态语言参数 (e.g., en_us, zh)
        speech_threshold: '0.5', // 语音阈值，用户建议
        token: apiKey
      });
      
      const wsUrl = `wss://streaming.assemblyai.com/v3/ws?${urlParams.toString()}`;
      const urlLogText = state.language === 'zh' 
        ? `使用${languageForLog}语音识别的 Streaming API v3 WebSocket`
        : 'Using Streaming API v3 WebSocket with browser authentication';
      console.log(`🔗 ${urlLogText}`);
      console.log('🧪 Streaming params:', { language_code: state.language, speech_threshold: '0.5' });

      setState(prev => ({ ...prev, isProcessing: true }));

      // 设置连接超时（15秒）
      connectionTimeoutRef.current = setTimeout(() => {
        const timeoutText = state.language === 'zh' 
          ? 'WebSocket 连接超时 (15 秒)'
          : 'WebSocket connection timeout (15 seconds)';
        console.error(`❌ ${timeoutText}`);
        
        const errorMessage = state.language === 'zh' 
          ? '连接超时 - 请检查网络连接'
          : 'Connection timeout - please check network connection';
          
        setState(prev => ({ 
          ...prev, 
          error: errorMessage,
          isProcessing: false,
          connectionStatus: 'failed',
          diagnostics: { ...prev.diagnostics, lastError: errorMessage }
        }));
        
        if (websocketRef.current && websocketRef.current.readyState === WebSocket.CONNECTING) {
          websocketRef.current.close();
        }
      }, 15000);

      // 创建WebSocket连接
      websocketRef.current = new WebSocket(wsUrl);

      // 设置 WebSocket 事件处理器
      websocketRef.current.onopen = () => {
        const connectedText = state.language === 'zh' 
          ? '流式转录 WebSocket 连接已建立'
          : 'Streaming API v3 WebSocket connection established';
        console.log(`✅ ${connectedText}`);
        
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }

        isConnectedRef.current = true;
        reconnectAttempts.current = 0;
        audioChunkCountRef.current = 0;
        
        setState(prev => ({ 
          ...prev, 
          error: null, 
          isProcessing: false,
          connectionStatus: 'connected',
          shouldFallback: false,
          diagnostics: { ...prev.diagnostics, lastError: null, permissionDenied: false }
        }));

        const readyText = state.language === 'zh' 
          ? '语音识别会话准备就绪'
          : 'Streaming v3 session ready';
        console.log(`🎙️ ${readyText}`);
      };

      websocketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const messageText = state.language === 'zh' 
            ? '转录消息类型'
            : 'Streaming v3 message type';
          console.log(`📨 ${messageText}:`, data.type);

          if (data.type === 'Begin') {
            const sessionText = state.language === 'zh' 
              ? '转录会话开始，ID'
              : 'Streaming v3 session begins, ID';
            console.log(`🎙️ ${sessionText}:`, data.id);
            
            setState(prev => ({
              ...prev,
              sessionId: data.id
            }));
          } else if (data.type === 'Turn') {
            const transcript = data.transcript || '';
            const isFormatted = data.turn_is_formatted;
            
            const turnText = state.language === 'zh' ? '转录事件' : 'Turn event received';
            console.log(`🔄 ${turnText}:`, {
              isFormatted,
              transcript: transcript.substring(0, 50) + (transcript.length > 50 ? '...' : ''),
              fullLength: transcript.length
            });
            
            if (isFormatted) {
              // 这是格式化的最终转录
              setState(prev => {
                const segmentNumber = prev.processedSegments + 1;
                const speaker = segmentNumber % 2 === 1 ? speakers[0] : speakers[1];
                const timestamp = new Date().toLocaleTimeString();
                const newSegment = `[${timestamp}] ${speaker}: ${transcript}`;
                const newCumulative = prev.cumulativeTranscript 
                  ? `${prev.cumulativeTranscript}\n\n${newSegment}`
                  : newSegment;
                  
                const finalText = state.language === 'zh' ? '最终转录' : 'Final transcript';
                console.log(`✅ ${finalText} ${segmentNumber}:`, transcript);
                
                return {
                  ...prev,
                  liveTranscript: '',
                  cumulativeTranscript: newCumulative,
                  processedSegments: segmentNumber,
                  sessionTranscript: newCumulative
                };
              });
            } else {
              // 这是实时转录
              const liveText = state.language === 'zh' ? '实时转录更新' : 'Live transcript update';
              console.log(`📝 ${liveText}:`, transcript.substring(0, 50));
              
              setState(prev => ({
                ...prev,
                liveTranscript: transcript
              }));
            }
          } else if (data.type === 'Termination') {
            const terminationText = state.language === 'zh' ? '转录会话终止' : 'Streaming v3 session terminated';
            console.log(`⏹️ ${terminationText}`);
            
            const audioDuration = data.audio_duration_seconds;
            const sessionDuration = data.session_duration_seconds;
            
            const statsText = state.language === 'zh' 
              ? `会话统计: 音频=${audioDuration}s, 会话=${sessionDuration}s`
              : `Session stats: Audio=${audioDuration}s, Session=${sessionDuration}s`;
            console.log(statsText);
            
            isConnectedRef.current = false;
            setState(prev => ({ 
              ...prev, 
              connectionStatus: 'disconnected',
              sessionDuration: sessionDuration * 1000
            }));
          } else if (data.type === 'error') {
            const errorText = state.language === 'zh' ? '转录错误' : 'Transcription error';
            console.error(`❌ ${errorText}:`, data.error);
            
            setState(prev => ({
              ...prev,
              error: `${errorText}: ${data.error}`,
              diagnostics: { ...prev.diagnostics, lastError: `${errorText}: ${data.error}` }
            }));
          }
        } catch (error) {
          const parseErrorText = state.language === 'zh' 
            ? '解析转录消息错误'
            : 'Error parsing transcription message';
          console.error(`${parseErrorText}:`, error);
          
          setState(prev => ({
            ...prev,
            diagnostics: { ...prev.diagnostics, lastError: `${parseErrorText}: ${error}` }
          }));
        }
      };

      websocketRef.current.onerror = (error) => {
        console.error('❌ Streaming v3 WebSocket error:', error);
        
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        
        const errorMessage = state.language === 'zh' 
          ? 'Streaming v3 连接失败。请检查网络连接并重试。'
          : 'Streaming v3 connection failed. Please check your internet connection and try again.';
          
        setState(prev => ({ 
          ...prev, 
          error: errorMessage,
          isProcessing: false,
          connectionStatus: 'failed',
          diagnostics: { ...prev.diagnostics, lastError: errorMessage }
        }));
      };

      websocketRef.current.onclose = (event) => {
        const closeText = state.language === 'zh' ? 'Streaming v3 WebSocket 关闭' : 'Streaming v3 WebSocket closed';
        console.log(`🔌 ${closeText}:`, {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
        
        isConnectedRef.current = false;
        
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        
        let errorMessage = '';
        let connectionStatus: 'failed' | 'permission_denied' | 'disconnected' = 'disconnected';
        let shouldFallback = false;
        
        // 详细的WebSocket关闭代码处理
        if (event.code === 4001 || event.code === 1008) {
          errorMessage = state.language === 'zh' 
            ? '认证失败 - API 密钥可能无效或缺少流式权限'
            : 'Authentication failed - API Key may be invalid or lacks Streaming permissions';
          connectionStatus = 'permission_denied';
          shouldFallback = true;
          
          const fallbackText = state.language === 'zh' 
            ? '检测到认证错误 - 将回退到 Web Speech API'
            : 'Authentication error detected - will fallback to Web Speech API';
          console.log(`🔄 ${fallbackText}`);
        } else if (event.code === 4002) {
          errorMessage = state.language === 'zh' ? '无效的请求格式' : 'Invalid request format';
          connectionStatus = 'failed';
        } else if (event.code === 4003) {
          errorMessage = state.language === 'zh' ? '不支持的音频格式' : 'Unsupported audio format';
          connectionStatus = 'failed';
        } else if (event.code === 4004) {
          errorMessage = state.language === 'zh' ? '会话已过期' : 'Session expired';
          connectionStatus = 'failed';
        } else if (event.code === 1002) {
          errorMessage = state.language === 'zh' ? '无效的 WebSocket 参数' : 'Invalid WebSocket parameters';
          connectionStatus = 'failed';
        } else if (event.code === 1005) {
          // 1005: 正常关闭，但没有状态代码 - 通常是正常的会话结束
          if (event.wasClean) {
            const normalCloseText = state.language === 'zh' 
              ? '会话正常结束'
              : 'Session ended normally';
            console.log(`✅ ${normalCloseText}`);
            // 不设置错误信息，这是正常的关闭
            connectionStatus = 'disconnected';
          } else {
            errorMessage = state.language === 'zh' ? '连接异常关闭' : 'Connection closed abnormally';
            connectionStatus = 'failed';
          }
        } else if (event.code === 1006) {
          errorMessage = state.language === 'zh' ? '连接丢失 - 请检查网络连接' : 'Connection lost - Please check your internet connection';
          connectionStatus = 'failed';
        } else if (event.code === 1011) {
          errorMessage = state.language === 'zh' 
            ? '服务器错误 - AssemblyAI Streaming 可能暂时不可用'
            : 'Server error - AssemblyAI Streaming may be temporarily unavailable';
          connectionStatus = 'failed';
        } else if (event.code !== 1000 && event.reason) {
          errorMessage = state.language === 'zh' 
            ? `连接错误: ${event.reason}`
            : `Connection error: ${event.reason}`;
          connectionStatus = 'failed';
        } else if (event.code !== 1000) {
          errorMessage = state.language === 'zh' 
            ? `连接失败 (代码: ${event.code})`
            : `Connection failed (Code: ${event.code})`;
          connectionStatus = 'failed';
        }
        
        setState(prev => ({ 
          ...prev, 
          error: errorMessage || null,
          isProcessing: false,
          connectionStatus,
          shouldFallback,
          diagnostics: { 
            ...prev.diagnostics, 
            lastError: errorMessage || null,
            permissionDenied: event.code === 1008 || event.code === 4001
          }
        }));

        // 自动重连逻辑（仅在录音状态下且非致命错误）
        if (event.code !== 1000 && event.code !== 1008 && event.code !== 4001 && reconnectAttempts.current < maxReconnectAttempts && state.isRecording) {
          reconnectAttempts.current++;
          const delay = 2000 * reconnectAttempts.current;
          
          const reconnectText = state.language === 'zh' 
            ? `尝试重连 ${reconnectAttempts.current}/${maxReconnectAttempts} 在 ${delay}ms 后...`
            : `Attempting reconnection ${reconnectAttempts.current}/${maxReconnectAttempts} in ${delay}ms...`;
          console.log(`🔄 ${reconnectText}`);
          
          setTimeout(() => {
            connectToAssemblyAI();
          }, delay);
        }
      };

    } catch (error) {
      const connectErrorText = state.language === 'zh' 
        ? '连接转录服务错误'
        : 'Error connecting to transcription service';
      console.error(`${connectErrorText}:`, error);
      
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      
      const errorMessage = error instanceof Error ? error.message : (
        state.language === 'zh' ? '连接失败' : 'Connection failed'
      );
      
      setState(prev => ({ 
        ...prev, 
        error: errorMessage,
        isProcessing: false,
        connectionStatus: 'failed',
        diagnostics: { ...prev.diagnostics, lastError: errorMessage, networkReachable: false }
      }));
    }
  }, [state.isRecording, state.language, speakers]);

  // 现代音频处理设置 - 使用 AudioWorkletNode
  const setupModernAudioProcessing = useCallback(async () => {
    try {
      const setupText = state.language === 'zh' 
        ? '设置现代 AudioWorklet 音频处理'
        : 'Setting up modern AudioWorklet audio processing';
      console.log(`🎧 ${setupText}...`);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000, // 修正为16kHz
          channelCount: 1
        }
      });

      streamRef.current = stream;
      audioContextRef.current = new AudioContext({ sampleRate: 16000 }); // 修正为16kHz
      
      if (audioContextRef.current.sampleRate !== 16000) {
        const sampleRateText = state.language === 'zh' 
          ? `AudioContext 采样率为 ${audioContextRef.current.sampleRate}Hz，期望 16000Hz`
          : `AudioContext sample rate is ${audioContextRef.current.sampleRate}Hz, expected 16000Hz`;
        console.warn(`⚠️ ${sampleRateText}`);
      }

      // 加载 AudioWorklet 处理器
      await audioContextRef.current.audioWorklet.addModule('/audio-processor.js');
      
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      audioWorkletNodeRef.current = new AudioWorkletNode(audioContextRef.current, 'audio-processor');

      // 监听来自 AudioWorklet 的音频数据
      audioWorkletNodeRef.current.port.onmessage = (event) => {
        if (event.data.type === 'audioData' && websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN && isConnectedRef.current && !isPausedRef.current) {
          try {
            websocketRef.current.send(event.data.data);
            audioChunkCountRef.current++;

            if (audioChunkCountRef.current % 100 === 0) {
              const chunkText = state.language === 'zh'
                ? `已发送 ${audioChunkCountRef.current} 个音频块 (AudioWorklet)，最新大小`
                : `Sent ${audioChunkCountRef.current} audio chunks (AudioWorklet), latest size`;
              console.log(`🎵 ${chunkText}: ${event.data.data.byteLength} bytes`);
            }
          } catch (error) {
            const sendErrorText = state.language === 'zh'
              ? '从 AudioWorklet 发送音频数据错误'
              : 'Error sending audio data from AudioWorklet';
            console.error(`❌ ${sendErrorText}:`, error);
          }
        }
      };

      sourceRef.current.connect(audioWorkletNodeRef.current);

      const completeText = state.language === 'zh' 
        ? '现代 AudioWorklet 处理设置完成'
        : 'Modern AudioWorklet processing setup complete';
      console.log(`✅ ${completeText}`);
      
      console.log('🔊 Audio config:', {
        sampleRate: audioContextRef.current.sampleRate,
        processorType: 'AudioWorkletNode'
      });

    } catch (error) {
      const setupErrorText = state.language === 'zh' 
        ? '设置 AudioWorklet 错误'
        : 'Error setting up AudioWorklet';
      console.error(`${setupErrorText}:`, error);
      throw error;
    }
  }, [state.language]);

  const setupAudioProcessing = useCallback(async () => {
    try {
      if (isAudioWorkletSupported.current) {
        await setupModernAudioProcessing();
      } else {
        const notSupportedText = state.language === 'zh' 
          ? '此浏览器不支持现代音频处理，请升级浏览器'
          : 'This browser does not support modern audio processing, please upgrade your browser';
        throw new Error(notSupportedText);
      }
    } catch (error) {
      const audioErrorText = state.language === 'zh' ? '设置音频错误' : 'Error setting up audio';
      console.error(`${audioErrorText}:`, error);
      
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : (
          state.language === 'zh' ? '音频设置失败' : 'Audio setup failed'
        )
      }));
    }
  }, [setupModernAudioProcessing, state.language]);

  const startRecording = useCallback(async (): Promise<void> => {
    if (!state.isSupported) {
      const notSupportedText = state.language === 'zh' 
        ? '不支持 MediaRecorder'
        : 'MediaRecorder not supported';
      setState(prev => ({ ...prev, error: notSupportedText }));
      return;
    }

    try {
      const startText = state.language === 'zh' 
        ? '开始语音识别录音'
        : 'Starting speech recognition recording';
      console.log(`🎙️ ${startText}...`);
      
      setState(prev => ({
        ...prev,
        diagnostics: {
          ...prev.diagnostics,
          connectionAttempts: 0,
          lastError: null,
          permissionDenied: false
        }
      }));
      
      await connectToAssemblyAI();
      
      await new Promise((resolve, reject) => {
        const checkConnection = () => {
          if (isConnectedRef.current) {
            resolve(true);
          } else if (websocketRef.current?.readyState === WebSocket.CLOSED) {
            const connFailedText = state.language === 'zh' 
              ? 'WebSocket 连接失败'
              : 'WebSocket connection failed';
            reject(new Error(connFailedText));
          } else if (state.connectionStatus === 'failed') {
            const failedText = state.language === 'zh' ? '连接失败' : 'Connection failed';
            reject(new Error(failedText));
          } else if (state.connectionStatus === 'permission_denied') {
            const permissionText = state.language === 'zh' ? '权限被拒绝' : 'Permission denied';
            reject(new Error(permissionText));
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        
        setTimeout(() => {
          if (!isConnectedRef.current) {
            const timeoutText = state.language === 'zh' ? '连接超时' : 'Connection timeout';
            reject(new Error(timeoutText));
          }
        }, 8000);
        
        setTimeout(checkConnection, 100);
      });
      
      await setupAudioProcessing();

      sessionStartTime.current = Date.now();

      setState(prev => ({
        ...prev,
        isRecording: true,
        isProcessing: false,
        error: null,
        processedSegments: 0,
        liveTranscript: '',
        cumulativeTranscript: '',
        transcript: '',
        segments: []
      }));
      
      const successText = state.language === 'zh' 
        ? '语音识别录音启动成功'
        : 'Speech recognition recording started successfully';
      console.log(`✅ ${successText}`);

    } catch (error) {
      const startErrorText = state.language === 'zh' ? '启动录音错误' : 'Error starting recording';
      console.error(`${startErrorText}:`, error);
      
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : (
          state.language === 'zh' ? '启动录音失败' : 'Failed to start recording'
        ),
        isProcessing: false 
      }));
    }
  }, [state.isSupported, state.connectionStatus, state.language, connectToAssemblyAI, setupAudioProcessing]);

  const stopRecording = useCallback((): void => {
    if (state.isRecording) {
      const stopText = state.language === 'zh' 
        ? '停止语音识别录音'
        : 'Stopping speech recognition recording';
      console.log(`⏹️ ${stopText}...`);
      
      const summaryText = state.language === 'zh' 
        ? `会话摘要: 发送了 ${audioChunkCountRef.current} 个音频块`
        : `Session summary: sent ${audioChunkCountRef.current} audio chunks`;
      console.log(`📊 ${summaryText}`);
      
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      
      // 停止现代音频处理
      if (audioWorkletNodeRef.current) {
        audioWorkletNodeRef.current.disconnect();
        audioWorkletNodeRef.current = null;
      }
      
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
      }

      isConnectedRef.current = false;
      isPausedRef.current = false;

      setState(prev => ({
        ...prev,
        isRecording: false,
        isPaused: false,
        isProcessing: false,
        transcript: prev.cumulativeTranscript || '',
        sessionTranscript: prev.cumulativeTranscript || '',
        sessionDuration: Date.now() - sessionStartTime.current,
        connectionStatus: 'disconnected'
      }));
      
      const stoppedText = state.language === 'zh' 
        ? '语音识别录音已停止'
        : 'Speech recognition recording stopped';
      console.log(`✅ ${stoppedText}`);
    }
  }, [state.isRecording, state.language]);

  const resetTranscript = useCallback(() => {
    isPausedRef.current = false;
    setState(prev => ({
      ...prev,
      transcript: '',
      segments: [],
      liveTranscript: '',
      cumulativeTranscript: '',
      error: null,
      processedSegments: 0,
      isPaused: false
    }));
  }, []);

  const resetSession = useCallback(() => {
    isPausedRef.current = false;
    setState(prev => ({
      ...prev,
      transcript: '',
      segments: [],
      liveTranscript: '',
      cumulativeTranscript: '',
      sessionTranscript: '',
      sessionSegments: [],
      sessionDuration: 0,
      error: null,
      processedSegments: 0,
      isPaused: false,
      sessionId: `assembly_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      connectionStatus: 'disconnected',
      apiKeyValid: null,
      shouldFallback: false,
      diagnostics: {
        apiKeyValid: null,
        networkReachable: null,
        websocketSupported: typeof WebSocket !== 'undefined',
        lastError: null,
        connectionAttempts: 0,
        permissionDenied: false
      }
    }));
  }, []);

  const setLanguage = useCallback((language: string) => {
    setState(prev => ({ ...prev, language }));
  }, []);

  const retryConnection = useCallback(() => {
    reconnectAttempts.current = 0;
    setState(prev => ({
      ...prev,
      shouldFallback: false,
      diagnostics: { ...prev.diagnostics, connectionAttempts: 0, lastError: null, permissionDenied: false }
    }));
    connectToAssemblyAI();
  }, [connectToAssemblyAI]);

  useEffect(() => {
    return () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
      if (audioWorkletNodeRef.current) {
        audioWorkletNodeRef.current.disconnect();
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (websocketRef.current) {
        websocketRef.current.close();
      }
    };
  }, []);

  const pauseRecording = useCallback((): void => {
    if (state.isRecording && !state.isPaused) {
      const pauseText = state.language === 'zh'
        ? '暂停语音识别录音'
        : 'Pausing speech recognition recording';
      console.log(`⏸️ ${pauseText}...`);

      isPausedRef.current = true;
      setState(prev => ({ ...prev, isPaused: true }));
    }
  }, [state.isRecording, state.isPaused, state.language]);

  const resumeRecording = useCallback((): void => {
    if (state.isRecording && state.isPaused) {
      const resumeText = state.language === 'zh'
        ? '恢复语音识别录音'
        : 'Resuming speech recognition recording';
      console.log(`▶️ ${resumeText}...`);

      isPausedRef.current = false;
      setState(prev => ({ ...prev, isPaused: false }));
    }
  }, [state.isRecording, state.isPaused, state.language]);

  return {
    ...state,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetTranscript,
    resetSession,
    setLanguage,
    retryConnection
  };
};
